/* eslint-disable consistent-return */
import Tippy, { TippyProps } from '@tippyjs/react'
import cn from 'classnames'
import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Placement } from 'tippy.js'

import styles from './Popover.module.scss'
import { roundOffset } from './utils'

import { addEventListener } from '~lib/helpers'
import { css, formatSizing, insertStyles } from '~lib/styleUtils'
import usePossibleControlledState from '~shared/lib/usePossibleControlledState'

export type PopoverOffset = number | [number, number]
export type PopoverTrigger = 'click' | 'hover' | 'focus' | 'contextmenu'
export type PopoverPlacement = Placement

export type PopoverProps = HTMLAttributes<HTMLDivElement> & {
  appendTo?: TippyProps['appendTo']
  arrow?: boolean
  anchorEl: HTMLElement | null
  children: React.ReactNode
  disabled?: boolean
  followCursor?: boolean
  maxWidth?: number | string
  minWidth?: number | string
  offset?: PopoverOffset
  onChangeOpen?: (visible: boolean) => void
  open?: boolean
  placement?: PopoverPlacement
  trigger?: PopoverTrigger | PopoverTrigger[]
  width?: number | string
  lockBody?: boolean
  fitContent?: boolean
  matchParentWidth?: boolean
  onClose?: () => void
}

const DEBUG = false
const DEFAULT_OFFSET = 0
const DEFAULT_MIN_WIDTH = 176

function Popover({
  matchParentWidth,
  anchorEl,
  disabled,
  followCursor,
  offset = DEFAULT_OFFSET,
  open,
  role,
  className,
  width,
  maxWidth,
  minWidth = DEFAULT_MIN_WIDTH,
  placement = 'bottom-start',
  trigger = 'click',
  lockBody = false,
  onBlur,
  onChangeOpen,
  children,
  fitContent,
  ...popoverProps
}: PopoverProps) {
  const [openState, changeOpen] = usePossibleControlledState(false, open, onChangeOpen)

  const [contentElement, setContentElement] = useState<HTMLDivElement>(null)
  const [[posX, posY], setMousePos] = useState([0, 0])

  const normalizedTrigger = useMemo(
    (): PopoverTrigger[] => (typeof trigger === 'string' ? [trigger] : trigger),
    [trigger]
  )

  const normalizedOffset = useMemo(
    (): [number, number] => (typeof offset === 'number' ? [offset, offset] : offset),
    [offset]
  )

  const popperOptions = useMemo<TippyProps['popperOptions']>(
    () => ({
      strategy: 'fixed',
      modifiers: [
        // TODO fix issue with incorrect positioning when gpuAcceleration disabled
        // {
        //   name: 'computeStyles',
        //   options: {
        //     gpuAcceleration: false,
        //   },
        // },
        followCursor && {
          name: 'computeStyles',
          options: {
            roundOffsets: roundOffset({ contentElement, posX, posY }),
          },
        },
      ].filter(Boolean),
    }),
    [followCursor, contentElement, posX, posY]
  )

  const onClosePopover = useCallback(() => {
    changeOpen(false)
  }, [changeOpen])

  useEffect(() => {
    if (normalizedTrigger.includes('contextmenu')) {
      return addEventListener(anchorEl, 'contextmenu', (ev) => {
        ev.preventDefault()

        if (followCursor) {
          setMousePos([ev.clientX, ev.clientY])
        }
        // eslint-disable-next-line no-console
        if (DEBUG) console.log('contextmenu')
        if (openState) {
          onClosePopover()
        } else {
          changeOpen(!openState)
        }
      })
    }
  }, [anchorEl, changeOpen, followCursor, normalizedTrigger, onClosePopover, openState])

  useEffect(() => {
    if (normalizedTrigger.includes('hover') && openState) {
      return addEventListener(document, 'mousemove', (ev) => {
        if (!contentElement?.contains(ev.target as Node) && !anchorEl?.contains(ev.target as Node)) {
          // eslint-disable-next-line no-console
          if (DEBUG) console.log('mousemove')
          onClosePopover()
        }
      })
    }
  }, [anchorEl, changeOpen, contentElement, normalizedTrigger, onClosePopover, openState])

  useEffect(() => {
    if (
      (normalizedTrigger.includes('focus') ||
        normalizedTrigger.includes('click') ||
        normalizedTrigger.includes('contextmenu')) &&
      openState
    ) {
      return addEventListener(document, 'mousedown', (ev) => {
        if (!contentElement?.contains(ev.target as Node) && !anchorEl?.contains(ev.target as Node)) {
          // eslint-disable-next-line no-console
          if (DEBUG) console.log('mousedown')
          onClosePopover()
        }
      })
    }
  }, [anchorEl, changeOpen, contentElement, normalizedTrigger, onClosePopover, openState])

  useEffect(() => {
    if (normalizedTrigger.includes('hover')) {
      return addEventListener(anchorEl, 'mouseenter', (ev) => {
        if (followCursor) {
          setMousePos([ev.clientX, ev.clientY])
        }
        // eslint-disable-next-line no-console
        if (DEBUG) console.log('mouseenter')
        changeOpen(true)
      })
    }
  }, [anchorEl, changeOpen, followCursor, normalizedTrigger, openState])

  // To handle click and focus triggers together
  // we should detect if user focus element using click event and prevent call of click handler
  // the order of native events: mousedown -> focus -> mouseup -> click
  const ignoreOnClickOnceRef = useRef(false)

  useEffect(() => {
    if (normalizedTrigger.includes('focus') && !openState) {
      return addEventListener(anchorEl, 'mousedown', () => {
        if (document.activeElement !== anchorEl) ignoreOnClickOnceRef.current = true
      })
    }
  }, [anchorEl, changeOpen, normalizedTrigger, openState])

  useEffect(() => {
    if (normalizedTrigger.includes('focus') && !openState) {
      return addEventListener(anchorEl, 'focus', () => {
        changeOpen(true)
      })
    }
  }, [anchorEl, changeOpen, normalizedTrigger, openState])

  useEffect(() => {
    if (normalizedTrigger.includes('focus') || normalizedTrigger.includes('click')) {
      return addEventListener(anchorEl, 'click', (e: MouseEvent) => {
        if (followCursor) {
          setMousePos([e.clientX, e.clientY])
        }
        if (ignoreOnClickOnceRef.current) {
          ignoreOnClickOnceRef.current = false
          return
        }
        // eslint-disable-next-line no-console
        if (DEBUG) console.log('click')
        if (openState) {
          onClosePopover()
        } else {
          changeOpen(!openState)
        }
      })
    }
  }, [anchorEl, changeOpen, followCursor, normalizedTrigger, onClosePopover, openState])

  useEffect(() => {
    if (normalizedTrigger.includes('focus') && openState) {
      return addEventListener(anchorEl, 'blur', (ev) => {
        if (!ev.relatedTarget || !contentElement?.contains(ev.relatedTarget as Node)) {
          // eslint-disable-next-line no-console
          if (DEBUG) console.log('blur')
          onClosePopover()
        }
      })
    }
  }, [anchorEl, changeOpen, contentElement, normalizedTrigger, onClosePopover, openState])

  useEffect(() => {
    if (!lockBody) return

    if (openState) {
      css(document.body, { pointerEvents: 'none' })
    }

    return () => {
      css(document.body, { pointerEvents: 'auto' })
    }
  }, [lockBody, openState, normalizedTrigger])

  const contentStyles = useMemo(() => {
    if (matchParentWidth) return insertStyles({ display: 'inline-block' })

    if (!fitContent) {
      return insertStyles({
        maxWidth: formatSizing(maxWidth),
        minWidth: formatSizing(minWidth),
        width: formatSizing(width || anchorEl?.offsetWidth),
      })
    }

    return null
  }, [anchorEl?.offsetWidth, fitContent, matchParentWidth, maxWidth, minWidth, width])

  return (
    <Tippy
      offset={normalizedOffset}
      reference={anchorEl}
      duration={0}
      placement={placement}
      followCursor={followCursor}
      disabled={disabled}
      visible={openState}
      className={styles.container}
      popperOptions={popperOptions}
      interactive
      maxWidth={maxWidth}
      content={
        <div ref={setContentElement} style={contentStyles} className={cn(styles.content, className)}>
          {children}
        </div>
      }
      {...popoverProps}
    />
  )
}

export default Popover
